import { useEffect, useState, type ComponentType } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { GoogleIcon, MicrosoftIcon, SquareIcon, WixIcon } from './ProviderIcons'
import {
  isConnectUnavailable,
  useCalendars,
  useConnectCalendar,
  type CalendarProvider,
} from '@/api/calendars'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'

const schema = z.object({
  calendar_name: z.string().trim().min(1, 'name_required'),
})
type FormValues = z.infer<typeof schema>

interface Props {
  open: boolean
  onClose: () => void
  companyId: number | undefined
  /** Fournisseur déjà choisi (page Intégrations) : on saute l'étape du choix. */
  initialProvider?: CalendarProvider
  /** Opened from a form with unsaved changes: the redirect will lose them. */
  warnUnsaved?: boolean
}

type Step = 'provider' | 'name'

const PROVIDER_ICONS: Record<CalendarProvider, ComponentType<{ className?: string }>> = {
  google: GoogleIcon,
  microsoft: MicrosoftIcon,
  square: SquareIcon,
  wix: WixIcon,
}

/** Provider cards, in display order. */
const PROVIDERS: CalendarProvider[] = ['google', 'microsoft', 'square', 'wix']

/** i18n keys of the name step, per provider. */
const NAME_STEP_KEYS: Record<CalendarProvider, { title: string; redirect: string }> = {
  google: { title: 'manager.calendars.modal.title', redirect: 'manager.calendars.modal.redirectWarning' },
  microsoft: { title: 'manager.calendars.modal.titleMicrosoft', redirect: 'manager.calendars.modal.redirectWarningMicrosoft' },
  square: { title: 'manager.calendars.modal.titleSquare', redirect: 'manager.calendars.modal.redirectWarningSquare' },
  wix: { title: 'manager.calendars.modal.titleWix', redirect: 'manager.calendars.modal.redirectWarningWix' },
}

export function ConnectCalendarDialog({ open, onClose, companyId, initialProvider, warnUnsaved = false }: Props) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('provider')
  const [provider, setProvider] = useState<CalendarProvider | null>(null)
  const connect = useConnectCalendar(companyId)
  // Square et Wix sont au forfait Team depuis le 26 oct. 2026. Une entreprise déjà
  // reliée garde sa connexion et peut la refaire (le serveur applique la même règle).
  const { allowed, planFor } = usePlanRights()
  const { data: calendars } = useCalendars(companyId)
  const locked = (id: CalendarProvider) =>
    (id === 'square' || id === 'wix') && !allowed('crm_equipe') && calendars != null && !calendars.some((c) => c.provider === id)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { calendar_name: '' },
  })

  useEffect(() => {
    if (open) {
      setStep(initialProvider ? 'name' : 'provider')
      setProvider(initialProvider ?? null)
      form.reset({ calendar_name: '' })
    }
  }, [open, form, initialProvider])

  const onSubmit = async (values: FormValues) => {
    if (!provider || locked(provider)) return
    try {
      const url = await connect.mutateAsync({
        provider,
        calendar_name: values.calendar_name.trim(),
      })
      // Redirige vers Google/Microsoft pour le consentement OAuth.
      window.location.href = url
    } catch (e) {
      toast.error(
        provider === 'wix' && isConnectUnavailable(e)
          ? t('manager.calendars.wix.unavailable')
          : t('manager.calendars.toast.connectError'),
      )
    }
  }

  const ProviderIcon = provider ? PROVIDER_ICONS[provider] : GoogleIcon

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showCloseButton={false}
        className={`${step === 'provider' ? 'sm:max-w-xl' : 'sm:max-w-md'} overflow-hidden border-0 p-0 shadow-2xl`}
      >
        {/* Accent top */}
        <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-blue-400 to-blue-600" />

        {step === 'provider' ? (
          <>
            <div className="p-6 pb-0">
              <DialogTitle className="text-xl tracking-tight">
                {t('manager.calendars.providerStep.title')}
              </DialogTitle>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t('manager.calendars.providerStep.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 px-6 pt-4 pb-6">
              {PROVIDERS.map((id) => {
                const Icon = PROVIDER_ICONS[id]
                if (locked(id)) {
                  return (
                    <div
                      key={id}
                      className="flex flex-col items-center gap-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-5"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white dark:bg-slate-800 shadow-sm ring-1 ring-black/5 dark:ring-white/10 opacity-60">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-sm text-slate-500 dark:text-slate-400">
                          {t(`manager.calendars.providerStep.${id}`)}
                        </p>
                        <PlanBadge plan={planFor('crm_equipe')} className="mt-2" />
                      </div>
                    </div>
                  )
                }
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setProvider(id)
                      setStep('name')
                    }}
                    className="group flex flex-col items-center gap-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-5 transition-all hover:border-blue-400 hover:bg-blue-50/50 dark:hover:border-blue-500 dark:hover:bg-blue-950/20 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white dark:bg-slate-800 shadow-sm ring-1 ring-black/5 dark:ring-white/10 transition-transform group-hover:scale-110">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-sm text-slate-900 dark:text-white">
                        {t(`manager.calendars.providerStep.${id}`)}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {t(`manager.calendars.providerStep.${id}Desc`)}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          </>
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="p-6 pb-0">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/10 via-blue-400/10 to-blue-500/10 ring-1 ring-black/10 dark:ring-white/10">
                  <ProviderIcon className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-xl tracking-tight">
                    {t(NAME_STEP_KEYS[provider ?? 'google'].title)}
                  </DialogTitle>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {t('manager.calendars.modal.subtitle')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5 px-6 pt-4 pb-2">
              <div>
                <Label htmlFor="calendar_name" className="text-sm font-medium">
                  {t('manager.calendars.modal.nameLabel')}
                </Label>
                <Input
                  id="calendar_name"
                  autoFocus
                  placeholder={t('manager.calendars.modal.namePlaceholder')}
                  className="mt-1.5"
                  {...form.register('calendar_name')}
                />
                {form.formState.errors.calendar_name && (
                  <p className="text-xs text-red-500 mt-1">
                    {t(
                      `manager.calendars.errors.${form.formState.errors.calendar_name.message}`,
                    )}
                  </p>
                )}
              </div>

              {provider && locked(provider) && <PlanBadge plan={planFor('crm_equipe')} />}

              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200/50 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20 px-3 py-2.5">
                <svg
                  className="w-4 h-4 text-amber-500 mt-0.5 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                  {t(NAME_STEP_KEYS[provider ?? 'google'].redirect)}
                  {warnUnsaved && (
                    <span className="block mt-1 font-medium">
                      {t('manager.calendars.modal.unsavedWarning')}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex justify-between gap-2 border-t bg-slate-50 dark:bg-slate-800/50 px-6 py-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep('provider')}
                className="gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('manager.calendars.modal.back')}
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={onClose}>
                  {t('manager.calendars.modal.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={connect.isPending || (!!provider && locked(provider))}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                >
                  {connect.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ProviderIcon className={provider === 'wix' ? 'w-4 h-4 text-white' : 'w-4 h-4'} />
                  )}
                  {t('manager.calendars.modal.continue')}
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
